/**
 * The admin console's decisions, without a console.
 *
 * Everything on that screen that is a *judgement* rather than a layout is
 * here: which tiles the stats become, what "6 people" versus "12 people,
 * raised by an admin" reads as, how a byte count is said out loud, and how
 * long ago somebody was last here. The screen renders these; it does not make
 * them. That is the same split `src/lib/receipt-library.ts` and
 * `src/lib/split-draft.ts` make, and for the same reason — the half that can
 * be wrong is the half that is pure functions of a value, so the tests can
 * reach it without a DOM.
 *
 * **The one thing this module could not render if it wanted to is money.**
 * There is no field on an `AdminUser` that holds an amount, so there is no
 * formatter here that could turn one into a sentence, and
 * `src/lib/format.ts` — the only place in this app a number becomes an
 * amount — is deliberately not imported. See `server/admin.ts` for the end of
 * this that is enforced rather than merely observed.
 */

import type {
  AccountState,
  AdminAuditEntry,
  AdminLimit,
  AdminOverride,
  AdminStats,
  AdminUser,
} from '../api'

/** Which pile of accounts the table is showing. */
export type StatusFilter = 'any' | 'active' | 'suspended' | 'deactivated'

/**
 * The filter is over the *column*, and the labels say so in the console's own
 * words.
 *
 * `suspended` is one value covering two states — a disable and a ban differ by
 * a row in the blocklist, not by a word in `users.status` — so this list
 * cannot offer them separately without asking the server a question it would
 * have to answer by re-deriving what a ban is in SQL. That is the same second
 * copy of a rule `server/admin.ts` refuses to keep for `override ?? plan`. So
 * the filter narrows to the column and the rows themselves say which is which,
 * which is where somebody scanning for a ban is looking anyway.
 */
export const STATUS_FILTERS: { id: StatusFilter; label: string }[] = [
  { id: 'any', label: 'Everyone' },
  { id: 'active', label: 'Active' },
  { id: 'suspended', label: 'Disabled or banned' },
  { id: 'deactivated', label: 'Deleted' },
]

/**
 * The override filters, and why they are keyed rather than a yes/no.
 *
 * "Whose uploads are off" is not a question about a column — the answer is
 * `override ?? plan`, and only `server/limits.ts` is allowed to work that out.
 * "Whose attachment switch has somebody touched" *is* a question about a
 * column, it is the question an admin actually arrives with, and it is the one
 * this list asks.
 */
export const OVERRIDE_FILTERS: { id: string; label: string }[] = [
  { id: 'any', label: 'Any account' },
  { id: 'some', label: 'Has an override' },
  { id: 'max_group_size', label: 'Group size raised' },
  { id: 'can_upload_attachments', label: 'Attachment switch set' },
  { id: 'storage_bytes', label: 'Storage changed' },
  { id: 'max_file_bytes', label: 'File size changed' },
]

/** One tile: a number, what it counts, and the sentence under it. */
export type Tile = { key: string; label: string; value: string; hint: string }

const count = (n: number) => n.toLocaleString('en-US')

const people = (n: number) => (n === 1 ? '1 account' : `${count(n)} accounts`)

/**
 * The tiles, in the order they are drawn.
 *
 * Each one carries the sentence that says what it is counting, because a bare
 * number on a dashboard is a number somebody will read as whatever they came
 * looking for. "1,006" over "Accounts" and "4" over "Seen this week" are
 * different claims and the second is worthless without its window.
 */
export function tilesFor(stats: AdminStats): Tile[] {
  const plans = stats.plans.map((plan) => `${count(plan.people)} on ${plan.name}`).join(', ')

  return [
    {
      key: 'accounts',
      label: 'Accounts',
      value: count(stats.people.total),
      // The counts are over `users.status`, and `suspended` is one column value
      // covering two of the console's states — so the tile says both rather
      // than picking one and being wrong about the other half of them.
      hint:
        `${count(stats.people.active)} active` +
        (stats.people.suspended > 0
          ? `, ${count(stats.people.suspended)} disabled or banned`
          : '') +
        (stats.people.deactivated > 0 ? `, ${count(stats.people.deactivated)} deleted` : ''),
    },
    {
      key: 'plans',
      label: 'Plans',
      value: count(stats.plans.reduce((total, plan) => total + plan.people, 0)),
      hint: plans || 'no plans configured',
    },
    {
      key: 'overrides',
      label: 'Overrides in force',
      value: count(stats.overrides.live),
      // The number of *accounts* is the one a support conversation is about;
      // the number of rows is what the tile counts, and they are rarely equal.
      hint: `across ${people(stats.overrides.people)}`,
    },
    {
      key: 'groups',
      label: 'Groups',
      value: count(stats.groups.total),
      hint: `${count(stats.groups.live)} not archived`,
    },
    {
      key: 'storage',
      label: 'Stored',
      value: formatBytes(stats.storage.bytes),
      hint: `${count(stats.storage.files)} ${stats.storage.files === 1 ? 'file' : 'files'}`,
    },
    {
      key: 'seen',
      label: 'Seen this week',
      value: count(stats.seen.week),
      hint:
        `${count(stats.seen.month)} in the last 30 days` +
        (stats.seen.never > 0 ? `, ${count(stats.seen.never)} never here` : ''),
    },
    {
      key: 'placeholders',
      label: 'Placeholders',
      value: count(stats.people.placeholders),
      hint: 'named by somebody, never signed in',
    },
    {
      key: 'admins',
      label: 'Admins',
      value: count(stats.people.admins),
      hint: 'can see this screen',
    },
  ]
}

/**
 * Bytes, in the unit a person would say them in.
 *
 * Powers of 1024 with the SI-ish short names, which is what every other
 * storage reading in this app uses. It is not `src/lib/format.ts`'s job: that
 * module turns integers into *money*, and the one thing this screen must never
 * do is look like it is doing that.
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const units = ['kB', 'MB', 'GB', 'TB']
  let value = bytes / 1024
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value >= 10 || Number.isInteger(value) ? Math.round(value) : value.toFixed(1)} ${units[unit]}`
}

/**
 * The effective group ceiling, as the sentence the column is for.
 *
 * `6` and `12` are the same kind of digit and completely different facts, and
 * the difference — that somebody was raised, by hand, and when it runs out —
 * is the entire reason an admin is looking at this column. So the provenance
 * is in the words rather than only in a colour or a badge.
 */
export function describeGroupLimit(limit: AdminLimit): string {
  const size = limit.unlimited ? 'No limit' : `${limit.value} people`
  if (limit.source !== 'override') return size
  return limit.expiresAt === null ? `${size} — raised` : `${size} — raised until ${day(limit.expiresAt)}`
}

/** The attachment switch, said the way the button it greys out says it. */
export function describeUploads(uploads: AdminUser['uploads']): string {
  if (!uploads.allowed) {
    return uploads.expiresAt === null ? 'Off' : `Off until ${day(uploads.expiresAt)}`
  }
  return uploads.source === 'override' ? 'On — set by an admin' : 'On'
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

/**
 * `2026-09-03 12:00:00` → `3 Sep 2026`.
 *
 * Spelled out rather than handed to `toLocaleDateString`, which answers "Sep"
 * or "Sept" depending on which version of ICU the runtime was built with — a
 * difference between Node and a browser is a test that passes here and a
 * screen that says something else. Every timestamp in this database is
 * `datetime('now')`, which is UTC and carries no zone, so it is read as UTC:
 * read as local time, every date on this screen would be a day out for half
 * the world.
 */
export function day(timestamp: string): string {
  const date = new Date(timestamp.replace(' ', 'T') + (timestamp.includes('Z') ? '' : 'Z'))
  if (Number.isNaN(date.getTime())) return timestamp
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`
}

/**
 * How long ago somebody was here.
 *
 * `null` is "Never", not "just now" and not a blank: an account nobody has
 * ever signed into is a placeholder or an abandoned invite, and that is a
 * different thing from one whose timestamp failed to load. `now` is a
 * parameter so the answer is a function of its inputs and the test does not
 * have to freeze a clock.
 */
export function describeLastSeen(lastSeenAt: string | null, now: Date = new Date()): string {
  if (lastSeenAt === null) return 'Never'

  const at = new Date(lastSeenAt.replace(' ', 'T') + (lastSeenAt.includes('Z') ? '' : 'Z'))
  if (Number.isNaN(at.getTime())) return lastSeenAt

  const minutes = Math.floor((now.getTime() - at.getTime()) / 60000)
  if (minutes < 0) return 'Just now'
  if (minutes < 5) return 'Just now'
  if (minutes < 60) return `${minutes} min ago`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`

  const days = Math.floor(hours / 24)
  if (days < 30) return days === 1 ? 'Yesterday' : `${days} days ago`

  // Past a month, the date itself is more use than a count of days.
  return day(lastSeenAt)
}

/** What each state is called on the screen. */
const STATE_LABELS: Record<AccountState, string> = {
  active: 'Active',
  disabled: 'Disabled',
  banned: 'Banned',
  deleted: 'Deleted',
}

export const describeState = (state: AccountState): string => STATE_LABELS[state] ?? state

/** What an account *is*, for the badge beside its name. */
export function describeKind(user: AdminUser): string[] {
  const badges: string[] = []
  if (user.isAdmin) badges.push('Admin')
  if (user.isPlaceholder) badges.push('Placeholder')
  if (user.state !== 'active') badges.push(describeState(user.state))
  return badges
}

/**
 * The three things an admin can do to an account, and the one that undoes two
 * of them.
 *
 * Each carries the sentence it has to be read before it is pressed, because
 * these are the buttons on this screen whose consequences are not obvious from
 * their labels: the difference between a disable and a ban is what happens
 * *next time*, and the thing worth saying about a delete is the thing it does
 * not do. `confirm` marks the two that ask again before they happen — one is
 * about somebody else's ability to come back and the other cannot be undone at
 * all.
 *
 * `from` is which states the action is offered in, so a screen never offers to
 * reinstate somebody who is already active, and nothing is offered at all for
 * an account that has been deleted.
 */
export type AccountAction = {
  state: AccountState
  label: string
  detail: string
  from: AccountState[]
  confirm: boolean
}

export const ACCOUNT_ACTIONS: AccountAction[] = [
  {
    state: 'disabled',
    label: 'Disable',
    detail:
      'They cannot sign in, on this browser or any other, from the next request. Nothing else changes and everything they are part of stays exactly as it is. One click puts it back.',
    from: ['active', 'banned'],
    confirm: false,
  },
  {
    state: 'banned',
    label: 'Ban',
    detail:
      'A disable, and their Google account and address are blocklisted as well — so signing in again makes no new account, and an invite cannot bring them back. Their expenses and everything they owe or are owed stay where they are.',
    from: ['active', 'disabled'],
    confirm: true,
  },
  {
    state: 'active',
    label: 'Reinstate',
    detail:
      'Signs them back in and lifts the blocklist. Nothing about their history has to be put back, because none of it was taken away.',
    from: ['disabled', 'banned'],
    confirm: false,
  },
  {
    state: 'deleted',
    label: 'Delete',
    detail:
      'Erases the name, the address and the Google account from the row, and removes every file they uploaded from storage. Their share of every expense stays, under a row with nobody in it, so nobody else’s balance moves. This cannot be undone.',
    from: ['active', 'disabled', 'banned'],
    confirm: true,
  },
]

/** Which of them to offer for an account in this state. */
export const actionsFor = (state: AccountState): AccountAction[] =>
  ACCOUNT_ACTIONS.filter((action) => action.from.includes(state))

/**
 * A limit key as a person would say it. Unknown keys fall through to the key
 * itself, because the list of them comes from the server — a key added to the
 * database and not yet to this map should read a little rough rather than
 * vanish off the screen.
 */
const LIMIT_LABELS: Record<string, string> = {
  max_group_size: 'People in a group',
  storage_bytes: 'Storage',
  max_file_bytes: 'One file',
  can_upload_attachments: 'Attachments',
  premium_themes: 'Premium themes',
  ocr: 'Reading receipts (OCR)',
}

export const describeLimitKey = (key: string): string =>
  LIMIT_LABELS[key] ?? key.replace(/_/g, ' ')

/**
 * What was typed into the override's value box, as the number the API takes.
 *
 * Empty, `unlimited` and `none` are all `null`, which is unlimited in both
 * tables, in the resolver and in the request — and never zero. Anything else
 * has to be a whole number that is not negative, and it is refused *here*
 * rather than sent, because "value must be a whole number" arriving as a 400
 * after a round trip is the same sentence a second later.
 */
export type ParsedLimit = { ok: true; value: number | null } | { ok: false; error: string }

export function parseLimitValue(typed: string): ParsedLimit {
  const trimmed = typed.trim().toLowerCase()
  if (trimmed === '' || trimmed === 'unlimited' || trimmed === 'none') {
    return { ok: true, value: null }
  }
  if (!/^\d+$/.test(trimmed)) {
    return { ok: false, error: 'A whole number, or blank for no limit.' }
  }
  const value = Number(trimmed)
  if (!Number.isSafeInteger(value)) return { ok: false, error: 'That number is too big.' }
  return { ok: true, value }
}

/** One override row, as the line under its key. */
export function describeOverride(override: AdminOverride): string {
  const value = override.unlimited ? 'No limit' : String(override.value)
  const when = override.expiresAt === null ? '' : ` until ${day(override.expiresAt)}`
  const who = override.setByName ? ` by ${override.setByName}` : ''
  return override.live
    ? `${value}${when}, set${who} on ${day(override.setAt)}`
    : `${value} — lapsed ${day(override.expiresAt ?? override.setAt)}`
}

/** One audit entry as a sentence, with the values it moved between. */
export function describeAudit(entry: AdminAuditEntry): string {
  const who = entry.byName ?? entry.by ?? 'somebody'
  const said = (value: unknown): string => {
    if (value === null || value === undefined) return 'nothing'
    if (typeof value === 'object') {
      const limit = value as { value?: number | null; expiresAt?: string | null }
      const number = limit.value === null ? 'no limit' : String(limit.value)
      return limit.expiresAt ? `${number} until ${day(limit.expiresAt)}` : number
    }
    return String(value)
  }

  const key = entry.key === null ? 'something' : describeLimitKey(entry.key)
  return `${who} changed ${key} from ${said(entry.before)} to ${said(entry.after)}`
}

/**
 * What to say when a filter matches nothing.
 *
 * Naming the filter is the point: "no accounts" in front of somebody who has
 * a search term and a status set is a screen implying the deployment is empty.
 */
export function emptyMessage(query: { q?: string; status?: string; overrides?: string }): string {
  const narrowed: string[] = []
  if (query.q?.trim()) narrowed.push(`matching “${query.q.trim()}”`)
  if (query.status && query.status !== 'any') narrowed.push(`that are ${query.status}`)
  if (query.overrides && query.overrides !== 'any') {
    narrowed.push(
      query.overrides === 'some'
        ? 'with an override'
        : `with an override on ${query.overrides.replace(/_/g, ' ')}`,
    )
  }

  if (narrowed.length === 0) return 'There are no accounts yet.'
  return `No accounts ${narrowed.join(', ')}.`
}
